/**
 * @file index.tsx
 *
 * @fileoverview Renders a component.
 */
import React from 'react';
import { FormGroup } from '~/vcl/molecules/01-form/02-form-group';
import FormItemsWidget from '../FormItems';
import { useIntl } from 'react-intl';
import { EntityDefinition } from '~/jobiqo/models/entity';
import { FormGroupTopInfoSlot } from '~/jobiqo/components/Slots/FormGroupTopInfoSlot';
import { FormGroupDescriptionSlot } from '~/jobiqo/components/Slots/FormGroupDescriptionSlot';

interface FormGroupBoxProps {
  /**
   * The form group to be displayed. Includes the list of fields.
   */
  group: any;
  /**
   * If the form is valid or not.
   */
  isValid: boolean;
  /**
   * The bundle of the entity to be displayed (job_per_template, job_per_link).
   */
  bundle?: string;
  /**
   * Render prop for custom elements.
   */
  overrides?: {
    [key: string]: ({ field, setFieldValue, values }) => void;
  };
  /**
   * Form groups additional elements.
   */
  formGroupsChildren?: {
    [key: string]: ({ definition }) => React.ReactNode;
  };
  /**
   * The name of the entity to be displayed (node, user, profile).
   */
  name: string;
  /**
   * The definition for all the fields in the entity.
   */
  definition?: EntityDefinition;
}
export default function FormGroupBox({
  group,
  isValid,
  bundle,
  overrides,
  formGroupsChildren,
  name,
  definition,
}: FormGroupBoxProps) {
  const intl = useIntl();
  return (
    <FormGroup
      key={group.key}
      label={
        group.name &&
        !!intl.messages &&
        intl.messages?.[`config_form_${group.key}_name`]
          ? intl.messages[`config_form_${group.key}_name`]
          : group.name
      }
      description={
        group?.description && <FormGroupDescriptionSlot group={group} />
      }
      {...(group.icon ? { icon: group.icon } : null)}
      valid={isValid}
    >
      <FormGroupTopInfoSlot group={group} bundle={bundle} name={name} />
      <FormItemsWidget
        name={name}
        bundle={bundle}
        fields={group.fields}
        fieldRules={group.fieldRules}
        overrides={overrides}
      />
      {formGroupsChildren?.[group.key] &&
        formGroupsChildren[group.key]({ definition })}
    </FormGroup>
  );
}
